import { useEffect, useId, useState } from "react";
import type { TemplateFieldDef } from "./flyerTemplateDevService";
import styles from "./FlyerTemplateDevForm.module.css";

// ── Constants ────────────────────────────────────────────────────────────────

const VALID_FIELD_TYPES = ["text", "price", "duration", "list", "textarea"] as const;
type FieldType = (typeof VALID_FIELD_TYPES)[number];
const VALID_SET = new Set<string>(VALID_FIELD_TYPES);

const FIELD_TYPE_LABELS: Record<string, string> = {
    text: "Text",
    price: "Price",
    duration: "Duration",
    list: "List",
    textarea: "Long text",
};

// No starter templates — admins build their own per brand/category

// ── Helpers ──────────────────────────────────────────────────────────────────

function fieldsToJson(fields: TemplateFieldDef[]): string {
    return JSON.stringify(fields, null, 2);
}

function slugKey(label: string, index: number): string {
    const base = label
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");
    return base || `field_${index + 1}`;
}

function newEmptyField(index: number): TemplateFieldDef {
    return { key: `field_${index + 1}`, label: "", placeholder: "", required: false, field_type: "text" };
}

function parseJson(raw: string): { fields: TemplateFieldDef[]; error: string | null } {
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return { fields: [], error: "Invalid JSON — check syntax." };
    }
    if (!Array.isArray(parsed)) return { fields: [], error: "Must be a JSON array [ … ]." };
    const keys = new Set<string>();
    for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i] as Record<string, unknown>;
        if (typeof item !== "object" || item === null)
            return { fields: [], error: `Item #${i + 1} is not an object.` };
        if (!item.key || typeof item.key !== "string")
            return { fields: [], error: `Item #${i + 1} missing string "key".` };
        if (!item.label || typeof item.label !== "string")
            return { fields: [], error: `Item #${i + 1} missing string "label".` };
        if (item.field_type && !VALID_SET.has(item.field_type as string))
            return { fields: [], error: `Item #${i + 1}: unknown field_type "${item.field_type as string}". Valid: ${VALID_FIELD_TYPES.join(", ")}.` };
        if (keys.has(item.key as string))
            return { fields: [], error: `Duplicate key "${item.key as string}" at item #${i + 1}.` };
        keys.add(item.key as string);
    }
    return {
        fields: (parsed as Record<string, unknown>[]).map(item => ({
            key: item.key as string,
            label: item.label as string,
            placeholder: typeof item.placeholder === "string" ? item.placeholder : "",
            required: item.required === true,
            field_type: VALID_SET.has(item.field_type as string) ? (item.field_type as FieldType) : "text",
        })),
        error: null,
    };
}

// ── Component ────────────────────────────────────────────────────────────────

interface Props {
    fields: TemplateFieldDef[];
    onChange: (fields: TemplateFieldDef[]) => void;
}

type Tab = "fields" | "json";

export default function TemplateFieldsEditor({ fields, onChange }: Props) {
    const baseId = useId();
    const [tab, setTab] = useState<Tab>("fields");
    const [raw, setRaw] = useState(() => fieldsToJson(fields));
    const [jsonError, setJsonError] = useState<string | null>(null);

    // When parent resets fields to [] (after submit), clear textarea too
    useEffect(() => {
        if (fields.length === 0) {
            setRaw("[]");
            setJsonError(null);
        }
    }, [fields.length]);

    // Switch to JSON tab → serialize current fields into textarea
    const switchTab = (next: Tab) => {
        if (next === "json") {
            setRaw(fieldsToJson(fields));
            setJsonError(null);
        }
        setTab(next);
    };

    // ── JSON tab handlers ──
    const handleJsonChange = (value: string) => {
        setRaw(value);
        const { fields: parsed, error } = parseJson(value);
        setJsonError(error);
        if (!error) onChange(parsed);
    };

    const handleJsonBlur = () => {
        const { fields: parsed, error } = parseJson(raw);
        setJsonError(error);
        if (!error) {
            setRaw(fieldsToJson(parsed));
            onChange(parsed);
        }
    };

    // ── Fields tab helpers ──
    const updateAt = (index: number, patch: Partial<TemplateFieldDef>) => {
        const next = fields.map((f, i) => (i === index ? { ...f, ...patch } : f));
        onChange(next);
        setRaw(fieldsToJson(next));
    };

    const removeAt = (index: number) => {
        const next = fields.filter((_, i) => i !== index);
        onChange(next);
        setRaw(fieldsToJson(next));
    };

    const addField = () => {
        const next = [...fields, newEmptyField(fields.length)];
        onChange(next);
        setRaw(fieldsToJson(next));
    };

    return (
        <div className={styles.fieldsEditor}>
            {/* Header row: title + tabs */}
            <div className={styles.fieldsEditorHeader}>
                <span className={styles.fieldsEditorTitle}>
                    Form fields
                    <span className={styles.badge} style={{ marginLeft: "0.5rem", fontSize: "0.65rem" }}>
                        {fields.length}
                    </span>
                </span>
                <div className={styles.tabBar}>
                    <button
                        type="button"
                        className={`${styles.tab} ${tab === "fields" ? styles.tabActive : ""}`}
                        onClick={() => switchTab("fields")}
                    >
                        Fields
                    </button>
                    <button
                        type="button"
                        className={`${styles.tab} ${tab === "json" ? styles.tabActive : ""}`}
                        onClick={() => switchTab("json")}
                    >
                        JSON
                    </button>
                </div>
            </div>

            {/* ── Fields tab ── */}
            {tab === "fields" && (
                <>
                    <p className={styles.hint}>
                        Field keys the server LLM maps from the user&apos;s chat message (e.g. destination, price, duration).
                        For flyer/video templates users type in plain language — not <code>{`{placeholder}`}</code> forms.
                    </p>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button type="button" className={styles.addFieldBtn} onClick={addField}>
                            + Add field
                        </button>
                    </div>
                    {fields.length === 0 ? (
                        <p className={styles.hint}>No fields yet. Load a starter template or add one manually.</p>
                    ) : (
                        <div className={styles.fieldsList}>
                            {fields.map((field, index) => (
                                <div key={`${baseId}-${index}`} className={styles.fieldRow}>
                                    <div className={styles.fieldRowTop}>
                                        <span className={styles.fieldRowNum}>#{index + 1}</span>
                                        <button
                                            type="button"
                                            className={styles.removeFieldBtn}
                                            onClick={() => removeAt(index)}
                                            aria-label="Remove field"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                    <div className={styles.grid2}>
                                        <div className={styles.field}>
                                            <label htmlFor={`${baseId}-label-${index}`}>Label</label>
                                            <input
                                                id={`${baseId}-label-${index}`}
                                                className={styles.input}
                                                value={field.label}
                                                onChange={e => updateAt(index, { label: e.target.value })}
                                                onBlur={e => {
                                                    const label = e.target.value.trim();
                                                    if (label && (!field.key || field.key.startsWith("field_"))) {
                                                        updateAt(index, { key: slugKey(label, index) });
                                                    }
                                                }}
                                                placeholder="e.g. Destination"
                                            />
                                        </div>
                                        <div className={styles.field}>
                                            <label htmlFor={`${baseId}-key-${index}`}>Key</label>
                                            <input
                                                id={`${baseId}-key-${index}`}
                                                className={styles.input}
                                                value={field.key}
                                                onChange={e =>
                                                    updateAt(index, { key: e.target.value.replace(/\s+/g, "_").toLowerCase() })
                                                }
                                                placeholder="destination"
                                            />
                                        </div>
                                    </div>
                                    <div className={styles.grid3}>
                                        <div className={styles.field}>
                                            <label htmlFor={`${baseId}-type-${index}`}>Type</label>
                                            <select
                                                id={`${baseId}-type-${index}`}
                                                className={styles.select}
                                                value={field.field_type}
                                                onChange={e => updateAt(index, { field_type: e.target.value })}
                                            >
                                                {VALID_FIELD_TYPES.map(t => (
                                                    <option key={t} value={t}>{FIELD_TYPE_LABELS[t]}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className={styles.field}>
                                            <label htmlFor={`${baseId}-ph-${index}`}>Placeholder</label>
                                            <input
                                                id={`${baseId}-ph-${index}`}
                                                className={styles.input}
                                                value={field.placeholder}
                                                onChange={e => updateAt(index, { placeholder: e.target.value })}
                                                placeholder="Hint shown in the input"
                                            />
                                        </div>
                                        <div className={styles.fieldCheckbox}>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={field.required}
                                                    onChange={e => updateAt(index, { required: e.target.checked })}
                                                />
                                                Required
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* ── JSON tab ── */}
            {tab === "json" && (
                <>
                    <p className={styles.hint}>
                        Edit as JSON. Each object needs <code>key</code> + <code>label</code>. Optional:{" "}
                        <code>placeholder</code>, <code>required</code>, <code>field_type</code> (
                        {VALID_FIELD_TYPES.join(" · ")}).
                    </p>
                    <textarea
                        className={`${styles.textarea} ${styles.textareaMono}`}
                        style={{ minHeight: "240px" }}
                        value={raw}
                        onChange={e => handleJsonChange(e.target.value)}
                        onBlur={handleJsonBlur}
                        spellCheck={false}
                    />
                    {jsonError && <p className={styles.jsonError}>{jsonError}</p>}
                </>
            )}
        </div>
    );
}
